import { CheckCircle2, Clock3, ExternalLink, LoaderCircle, ShieldCheck, TriangleAlert } from "lucide-react";
import jsQR from "jsqr";
import QRCode from "qrcode";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS, type CheckoutData, type CheckoutCopy, type CheckoutTemplate } from "@/shared/contracts";
import { swrFetcher } from "@/web/api";
import brandLogo from "@/web/assets/brand-logo.png";
import { BRAND_NAME, BRAND_TAGLINE } from "@/web/lib/brand";
import { fillCopy } from "@/shared/checkout-copy";
import { Button } from "@/web/components/ui/button";
import { Loading } from "@/web/components/ui/loading";
import { formatDate } from "@/web/lib/utils";

function countdown(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function isMobileBrowser() {
  const browserNavigator = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (browserNavigator.userAgentData?.mobile === true) return true;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(browserNavigator.userAgent) ||
    (/Macintosh/i.test(browserNavigator.userAgent) && browserNavigator.maxTouchPoints > 1);
}

function Shell({
  children,
  className = "",
  theme = "classic",
  mode = "light",
  copy,
}: {
  children: ReactNode;
  className?: string;
  theme?: CheckoutTemplate;
  mode?: "light" | "dark";
  copy?: CheckoutCopy;
}) {
  return (
    <div
      className={`checkout-scope min-h-screen ${className}`}
      data-theme={theme}
      data-mode={mode}
      style={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}
    >
      {children}
    </div>
  );
}

function BrandHeader({ copy }: { copy?: CheckoutCopy }) {
  return (
    <header className="flex items-center justify-center gap-4">
      <img src={brandLogo} alt="" data-brand-logo className="h-14 w-14 shrink-0 object-contain sm:h-[68px] sm:w-[68px]" />
      <span className="flex flex-col leading-tight">
        <span className="text-[19px] font-semibold tracking-tight sm:text-[22px]">{copy?.brand_name || BRAND_NAME}</span>
        <span className="text-[13px] text-muted sm:text-[15px]">{copy?.brand_tagline || BRAND_TAGLINE}</span>
      </span>
    </header>
  );
}

export function CheckoutPage() {
  const { token = "" } = useParams();
  const [now, setNow] = useState(Date.now());
  const [qrLink, setQrLink] = useState("");
  const [upstreamQr, setUpstreamQr] = useState("");
  const [systemDark, setSystemDark] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const mobileRedirectAttempted = useRef(false);
  const { data, error, isLoading, mutate } = useSWR<CheckoutData>(`/public-api/checkout/${encodeURIComponent(token)}`, swrFetcher, {
    refreshInterval: (latest) => {
      if (latest && (["paid", "late_paid"].includes(latest.status) || Date.parse(latest.monitor_until) <= Date.now())) return 0;
      // 上游二维码还在后台抓取时用更短的间隔，尽快发现它
      if (latest?.channel === "upstream" && !latest.upstream_qrcode) return 3_000;
      return (latest?.payment_poll_interval_seconds ?? PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS) * 1000;
    },
    shouldRetryOnError: false,
  });
  const { data: generatedQr } = useSWR(
    data?.collection_mode === "transfer" && data.payment_uri ? ["transfer-qr", data.payment_uri] : null,
    ([, uri]) => QRCode.toDataURL(uri, { width: 320, margin: 2, errorCorrectionLevel: "M", color: { dark: "#0b1220", light: "#ffffff" } }),
  );

  // 上游订单：把上游返回的支付凭据渲染成本站收银台上的二维码
  // 等待超过 40 秒才回退用 payurl 生成二维码；提前声明避免暂时性死区。
  const qrFallbackReady = waitSeconds >= 40;

  useEffect(() => {
    // 自挂：收款码由服务端解码重画好，拿来即用，不存在等待过程
    if (data?.channel === "self_wx") {
      setUpstreamQr(data.self_wx_qrcode || "");
      return;
    }
    if (data?.channel !== "upstream") {
      setUpstreamQr("");
      return;
    }
    // 拿到上游纯二维码就直接用
    if (data.upstream_qrcode) {
      setUpstreamQr(data.upstream_qrcode);
      return;
    }
    // 还在抓取中：不给跳转链接的二维码顶着，先让用户看到明确的等待提示；
    // 只有等待超过 40 秒（可能抓取失败）才回退到 payurl 生成的二维码免得无法支付。
    if (!qrFallbackReady) {
      setUpstreamQr("");
      return;
    }
    const credential = data.upstream_payurl;
    if (!credential) {
      setUpstreamQr("");
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(credential, { width: 480, margin: 1, errorCorrectionLevel: "M" })
      .then((url) => { if (!cancelled) setUpstreamQr(url); })
      .catch(() => { if (!cancelled) setUpstreamQr(""); });
    return () => {
      cancelled = true;
    };
  }, [data?.channel, data?.upstream_payurl, data?.upstream_qrcode, data?.self_wx_qrcode, qrFallbackReady]);

  // 上游二维码是异步抓取的（整链约 30 秒），这里做一个可见的等待计时，
  // 避免用户看到一直转圈却不知道要等多久。
  const waitingQr = data?.channel === "upstream" && !data.upstream_qrcode && !upstreamQr && !qrFallbackReady;
  useEffect(() => {
    if (!waitingQr) {
      setWaitSeconds(0);
      return;
    }
    const timer = window.setInterval(() => setWaitSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [waitingQr]);

  // 等待上游二维码期间主动拉数据，不等 SWR 的 refreshInterval：
  // 后台是异步写入二维码的，前端必须自己发现它出现了。
  useEffect(() => {
    if (!waitingQr) return;
    const timer = window.setInterval(() => {
      void mutate();
    }, 3_000);
    return () => window.clearInterval(timer);
  }, [waitingQr, mutate]);

  // 「自动」配色：跟随访客的操作系统 / 浏览器深色偏好
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setSystemDark(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // 注入后台配置的自定义 CSS（后端已过滤危险标签，这里用 textContent 避免解析 HTML）
  useEffect(() => {
    const css = data?.checkout_custom_css;
    if (!css) return;
    const style = document.createElement("style");
    style.setAttribute("data-checkout-custom", "");
    style.textContent = css;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, [data?.checkout_custom_css]);

  useEffect(() => {
    const imageUrl = data?.collection_mode === "business_qr" ? data?.business_qr_url : "";
    if (!imageUrl) {
      setQrLink("");
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(imageUrl);
        const bitmap = await createImageBitmap(await response.blob());
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) return;
        context.drawImage(bitmap, 0, 0);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        const decoded = jsQR(image.data, image.width, image.height);
        if (!cancelled && decoded?.data) setQrLink(decoded.data);
      } catch {
        /* 解码失败时保留静态二维码，不影响主流程 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.collection_mode, data?.business_qr_url]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (
      mobileRedirectAttempted.current ||
      !data ||
      data.collection_mode !== "transfer" ||
      data.status !== "pending" ||
      !data.payment_uri ||
      Date.parse(data.expires_at) <= Date.now() ||
      !isMobileBrowser()
    ) return;

    mobileRedirectAttempted.current = true;
    window.location.assign(data.payment_uri);
  }, [data]);

  // 配色解析必须放在任何 return 之前，否则 early-return 分支会因暂时性死区抛错
  const colorModeSetting = data?.checkout_color_mode ?? "auto";
  // 文案配置（后端保证一定存在，这里再保守地回退一次）
  const copy = data?.checkout_copy;
  const txt = (value: string | undefined, fallback: string) => value || fallback;
  const fill = (value: string | undefined, fallback: string) =>
    fillCopy(value || fallback, { out_trade_no: data?.out_trade_no ?? "" });

  const resolvedMode: "light" | "dark" =
    colorModeSetting === "auto" ? (systemDark ? "dark" : "light") : colorModeSetting;

  if (isLoading) {
    return (
      <Shell theme={data?.checkout_template} mode={resolvedMode} copy={copy} className="flex items-center justify-center px-4">
        <Loading label="正在读取支付订单" />
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell theme={data?.checkout_template} mode={resolvedMode} copy={copy} className="flex items-center justify-center px-4">
        <div className="checkout-panel w-full max-w-sm rounded-2xl p-8 text-center">
          <TriangleAlert className="mx-auto size-8 text-destructive" />
          <h1 className="mt-4 text-lg font-semibold tracking-tight">订单不存在</h1>
          <p className="mt-2 text-sm leading-6 text-muted">链接无效或已失效。</p>
        </div>
      </Shell>
    );
  }

  const paid = data.status === "paid" || data.status === "late_paid";
  const checkoutExpired = now >= Date.parse(data.expires_at);
  const monitoringEnded = now >= Date.parse(data.monitor_until);
  const expiringSoon = !checkoutExpired && Date.parse(data.expires_at) - now < 60_000;
  const isUpstream = data.channel === "upstream";
  const isSelfWx = data.channel === "self_wx";
  // 微信类订单（上游 / 自挂）：二维码都由服务端给出，展示方式一致。
  const isWx = isUpstream || isSelfWx;
  // 微信支付在移动端没有能直达付款页的链接，只能唤起微信 App，
  // 所以给 weixin:// 让用户进微信后用长按识别或相册选图完成支付。
  const mobilePayHref = isWx
    ? "weixin://"
    : data.collection_mode === "transfer"
      ? data.payment_uri
      : qrLink;

  if (paid) {
    return (
      <Shell theme={data?.checkout_template} mode={resolvedMode} copy={copy} className="flex items-center justify-center px-4 py-10">
        <div className="checkout-panel w-full max-w-md rounded-2xl p-8 text-center">
          <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="size-8 text-success" />
          </span>
          <h1 className="mt-5 text-xl font-semibold tracking-tight">
            {txt(copy?.paid_title, "支付成功")}
          </h1>
          <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-success">¥{data.payable_money}</p>
          <p className="mt-3 text-sm text-muted">{fill(copy?.paid_note, "订单 {out_trade_no} 已完成。")}</p>
          {data.return_target ? (
            <Button className="mt-6 w-full" asChild>
              <a href={data.return_target}>{txt(copy?.paid_return, "返回商户页面")}<ExternalLink /></a>
            </Button>
          ) : null}
        </div>
      </Shell>
    );
  }

  if (monitoringEnded) {
    return (
      <Shell theme={data?.checkout_template} mode={resolvedMode} copy={copy} className="flex items-center justify-center px-4 py-10">
        <div className="checkout-panel w-full max-w-md rounded-2xl p-8 text-center">
          <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-destructive/10">
            <Clock3 className="size-8 text-destructive" />
          </span>
          <h1 className="mt-5 text-xl font-semibold tracking-tight">{txt(copy?.ended_title, "订单已结束")}</h1>
          <p className="mt-2 text-sm leading-6 text-muted">{txt(copy?.ended_note, "未收到这笔款项。请不要继续付款，返回商户重新下单。")}</p>
          <div className="mt-6 rounded-xl bg-foreground/[0.03] p-3.5 text-left">
            <p className="text-[11px] text-muted">{txt(copy?.label_trade_no, "订单号")}</p>
            <p className="mt-1 break-all font-mono text-xs">{data.out_trade_no}</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell theme={data.checkout_template} mode={resolvedMode} copy={copy} className="flex flex-col justify-center px-3 py-8 sm:px-4 sm:py-12">
      <div className="mx-auto" style={{ width: "100%", maxWidth: "58rem" }}>
        <BrandHeader copy={copy} />

        <div className="checkout-grid mt-8 min-w-0 space-y-5 md:grid md:gap-5 md:space-y-0 md:grid-cols-[minmax(0,1fr)_320px]">
          <section className="checkout-panel min-w-0 overflow-hidden rounded-2xl p-5 sm:p-7">
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-muted">{data.name}</p>
                <p className="mt-2 flex items-baseline text-3xl font-semibold tabular-nums tracking-tight sm:text-4xl">
                  <span className="mr-0.5 text-2xl">¥</span>
                  {data.payable_money}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium tabular-nums ${
                  checkoutExpired || expiringSoon ? "bg-destructive/[0.08] text-destructive" : "bg-foreground/[0.05] text-muted"
                }`}
              >
                {checkoutExpired ? "正在确认" : `剩余 ${countdown(Date.parse(data.expires_at) - now)}`}
              </span>
            </div>

            {data.payable_money !== data.requested_money ? (
              <div className="mt-5 rounded-xl bg-primary/[0.05] px-4 py-3.5 text-sm leading-6">
                <span className="font-semibold text-primary">{txt(copy?.surcharge_lead, "请按上方金额准确支付。")}</span>
                <span className="text-muted">{txt(copy?.surcharge_note, " 金额精确到分，多付或少付都无法自动确认。")}</span>
              </div>
            ) : null}

            <div className="mt-6 overflow-hidden">
              <div className="checkout-qr mx-auto min-w-0 w-full max-w-[300px] rounded-xl p-3 sm:max-w-[230px]">
                {isWx ? (
                  upstreamQr ? (
                    <img
                      src={upstreamQr}
                      alt="微信支付二维码"
                      className="mx-auto block h-auto max-h-[220px] w-full max-w-[220px] object-contain sm:max-h-[190px] sm:max-w-[190px]"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2.5 py-6 text-center">
                      <LoaderCircle className="size-5 animate-spin text-zinc-400" />
                      {/* 二维码容器是固定白底，这里的文字必须用深色，否则在深色主题下看不见 */}
                      <p className="text-xs font-medium text-zinc-800">正在获取支付信息</p>
                      <p className="text-[11px] leading-4 text-zinc-500">
                        预计最多 30 秒，请不要关闭页面
                      </p>
                      <p className="text-[11px] tabular-nums text-zinc-400">
                        {waitSeconds > 0 ? `已等待 ${waitSeconds} 秒` : "正在连接…"}
                      </p>
                      {waitSeconds >= 40 ? (
                        <p className="mt-1 text-[11px] leading-4 text-red-600">
                          获取较慢，已改用普通二维码，可直接扫码支付
                        </p>
                      ) : null}
                    </div>
                  )
                ) : data.collection_mode === "business_qr" ? (
                  <img
                    src={data.business_qr_url}
                    alt="支付宝经营码"
                    className="mx-auto block h-auto max-h-[220px] w-full max-w-[220px] object-contain sm:max-h-[190px] sm:max-w-[190px]"
                  />
                ) : generatedQr ? (
                  <img src={generatedQr} alt="支付宝转账二维码" className="size-[220px] max-w-full object-contain sm:size-[190px]" />
                ) : (
                  <Loading label="正在生成二维码" />
                )}
              </div>
            </div>

            {mobilePayHref ? (
              <Button className="mt-4 w-full md:hidden" asChild>
                <a href={mobilePayHref}>{isWx ? txt(copy?.button_open_wxpay, "打开微信") : txt(copy?.button_open_alipay, "打开支付宝付款")}<ExternalLink /></a>
              </Button>
            ) : null}

            <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-foreground/[0.035] px-4 py-3.5">
              <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success" />
              <p className="text-xs leading-5 text-muted">
                {isWx
                  ? txt(copy?.guide_wxpay, "请用微信扫描上方二维码完成支付，支付结果会自动同步。")
                  : data.collection_mode === "business_qr"
                    ? qrLink
                      ? txt(copy?.guide_alipay_deeplink, "点击上方按钮直达支付宝付款，也可长按二维码识别。金额请按上方显示输入。")
                      : txt(copy?.guide_alipay_qr, "打开支付宝扫描上方二维码，并输入显示的金额。")
                    : fill(copy?.guide_transfer, "打开支付宝扫码转账，备注请保持为 {out_trade_no}。")}
              </p>
            </div>

            {checkoutExpired ? (
              <div className="mt-3 flex gap-2.5 rounded-xl border border-destructive/25 bg-destructive/[0.04] p-3.5">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
                <p className="text-xs leading-5 text-muted">
                  {txt(copy?.expired_note, "如果你已经付款，请稍候，系统仍在确认中。如尚未付款，请返回商户重新下单。")}
                </p>
              </div>
            ) : null}
          </section>

          <aside className="min-w-0 overflow-hidden space-y-4">
            <div className="checkout-panel rounded-2xl p-5 sm:p-6">
              <h2 className="text-base font-semibold">{txt(copy?.aside_title, "订单信息")}</h2>
              <dl className="mt-5 space-y-4">
                <div>
                  <dt className="text-xs text-muted">{txt(copy?.label_trade_no, "订单号")}</dt>
                  <dd className="mt-1.5 break-all font-mono text-sm">{data.out_trade_no}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">{txt(copy?.label_created_at, "创建时间")}</dt>
                  <dd className="mt-1.5 text-base">{formatDate(data.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted">{txt(copy?.label_remaining, "自动确认剩余")}</dt>
                  <dd className="mt-1.5 font-mono text-base tabular-nums">{countdown(Date.parse(data.monitor_until) - now)}</dd>
                </div>
              </dl>
            </div>

            <div className="checkout-panel rounded-2xl p-5 text-sm leading-6 text-muted">
              <p>{txt(copy?.aside_note, "支付完成后本页面会自动确认，请不要重复支付。")}</p>
            </div>
          </aside>
        </div>
      </div>
    </Shell>
  );
}
